import React, { Component, type ErrorInfo } from 'react';

import { OPERATIONAL_EVENT_TYPE } from '@atlaskit/analytics-gas-types';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { Dispatch } from '@atlassian/forge-ui-types/components';
import { ProductEnvironment } from '@atlassian/forge-ui-types/environments';

import { FORGE_UI_ANALYTICS_CHANNEL } from '../analytics';
import {
	useForgeUIRuntime,
	type ForgeUIRuntimeType,
} from '../analytics/ForgeUIRuntimeAnalyticsContext';
import ErrorPanel from '../components/UIKit1/errorPanel/errorPanel';
import { MetricsContext } from '../context';
import { captureAndReportError } from '../error-reporting';

export class ComponentError extends Error {
	component: string;

	constructor(message: string, component: string) {
		super(message);
		this.component = component;
		// https://github.com/Microsoft/TypeScript/wiki/Breaking-Changes#extending-built-ins-like-error-array-and-map-may-no-longer-work
		Object.setPrototypeOf(this, ComponentError.prototype);
	}
}

interface Props {
	children?: React.ReactNode;
	dispatch?: Dispatch;
	extension?: Record<string, any>;
}

interface ComponentErrorBoundaryWithCreateAnalyticsEventProps extends Props {
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	runtime?: ForgeUIRuntimeType;
}

interface State {
	thrownError?: {
		error: Error;
		errorInfo: ErrorInfo;
	};
}
class ComponentErrorBoundaryWithCreateAnalyticsEvent extends Component<
	ComponentErrorBoundaryWithCreateAnalyticsEventProps,
	State
> {
	static contextType = MetricsContext;
	context!: React.ContextType<typeof MetricsContext>;

	public state: State = {
		thrownError: undefined,
	};

	public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		// Filters out reporting errors for components that are not valid e.g. <Buton/>
		if (!(error instanceof ComponentError)) {
			throw error;
		}

		const { extension, runtime } = this.props;
		const { page, environment } = this.context;

		captureAndReportError({
			error,
			errorInfo,
			page,
			runtime,
			environment: environment ?? ProductEnvironment.DEVELOPMENT,
			errorExtensionDetails: extension
				? {
						type: extension.type,
						environmentType: extension.environmentType,
						appOwnerAccountId: extension.appOwner?.accountId,
						properties: extension.properties,
						id: extension?.id,
					}
				: undefined,
		});

		this.props
			.createAnalyticsEvent({
				eventType: OPERATIONAL_EVENT_TYPE,
				data: {
					action: 'failed',
					actionSubject: 'forge.ui.renderer',
					source: page,
					tags: ['forge'],
					attributes: {
						target: 'errorBoundary',
						errorName: 'componentError',
						component: error.component,
					},
				},
			})
			.fire(FORGE_UI_ANALYTICS_CHANNEL);

		this.setState({
			thrownError: {
				error,
				errorInfo,
			},
		});
	}

	public render() {
		const { thrownError } = this.state;
		if (!thrownError) {
			return this.props.children;
		}

		return <ErrorPanel error={thrownError.error} dispatch={this.props.dispatch} />;
	}
}

const ComponentErrorBoundary = (props: Props): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const runtime = useForgeUIRuntime();

	return (
		<ComponentErrorBoundaryWithCreateAnalyticsEvent
			{...props}
			createAnalyticsEvent={createAnalyticsEvent}
			runtime={runtime}
		/>
	);
};

export default ComponentErrorBoundary;
